import type { Agency, AgencyPriceRange } from '@saaga/shared/interfaces/agencies.interface'
import type { AgencyListResponse, AgencyResponse } from '@saaga/shared/interfaces/agency.interface'

import { filterKnownCategoryList } from '@/components/agencies/agency-category.util'

const isDefaultAgency = (agency: AgencyResponse): boolean =>
  agency.is_default_free || agency.is_default_pro

/**
 * Maps a single AgencyResponse from the API to the Agency interface used by UI components.
 * Some fields like rating, reviewCount, usageCount are not available in the API
 * and are set to default values.
 */
export const mapAgencyToAgency = (response: AgencyResponse): Agency => ({
  id: response.id,
  userId: response.user_id,
  name: response.presentation_name,
  slug: response.slug,
  description: response.presentation_description_short ?? '',
  logoUrl: response.presentation_image_path,
  rating: 0,
  reviewCount: 0,
  usageCount: 0,
  priceRange: 1 as AgencyPriceRange,
  categoryList: filterKnownCategoryList(response.categories ?? []),
  organization: {
    id: response.tenant_id,
    name: response.presentation_distributor_name ?? '',
    logoUrl: null,
  },
  visibility: response.visibility,
  rejectionReason: response.rejection_reason,
  supportedInputTypeList: response.supported_input_types ?? ['text', 'image', 'pdf', 'code', 'csv'],
  createdAt: response.created_at,
  updatedAt: response.updated_at,
})

/**
 * Maps a list response from the API to an array of Agency objects.
 * Filters out system/default agencies only in the marketplace (Explore) context.
 */
export const mapAgencyListToAgencyList = (
  response: AgencyListResponse,
  isMarketplace = false
): Agency[] =>
  response.items.filter((item) => !isMarketplace || !isDefaultAgency(item)).map(mapAgencyToAgency)
